import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ScrollText, ExternalLink, Award, BookOpen, Coins } from "lucide-react";

export default function PublicDossier() {
  const { id } = useParams();
  const [u, setU] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/public/professors/${id}`).then(r => setU(r.data)).catch(() => setErr("Dossier not found or not public"));
  }, [id]);

  if (err) return (
    <div className="min-h-screen paper flex items-center justify-center">
      <div className="text-center">
        <div className="font-display text-4xl">{err}</div>
        <Link to="/" className="text-primary hover:underline text-sm mt-3 inline-block">← Back to GrantForge</Link>
      </div>
    </div>
  );
  if (!u) return <div className="min-h-screen paper p-10 text-muted-foreground">Loading…</div>;

  return (
    <div className="min-h-screen paper">
      <header className="border-b border-border">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><ScrollText className="w-5 h-5 text-primary"/><span className="font-display text-2xl">GrantForge</span></Link>
          <div className="overline">Public PI Dossier</div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-6 py-14">
        <div className="flex items-start justify-between gap-8 mb-10">
          <div>
            <div className="overline">{u.discipline || "Researcher"}</div>
            <h1 className="font-display text-6xl mt-2 leading-none">{u.title} {u.full_name}</h1>
            <p className="text-muted-foreground mt-2">{u.institution || ""}</p>
            {u.bio && <p className="mt-6 max-w-2xl leading-relaxed">{u.bio}</p>}
            {(u.orcid || u.website) && (
              <div className="mt-4 flex flex-wrap gap-4 text-sm">
                {u.orcid && <span>ORCID: <b>{u.orcid}</b></span>}
                {u.website && <a href={u.website} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">{u.website} <ExternalLink className="w-3 h-3"/></a>}
              </div>
            )}
            {Array.isArray(u.expertise) && u.expertise.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-2">
                {u.expertise.map((e,i)=>(
                  <span key={i} className="px-3 py-1 text-xs rounded-full bg-primary/10 text-primary border border-primary/20">{e}</span>
                ))}
              </div>
            )}
          </div>
          <div className="w-28 h-28 rounded-full bg-primary/10 text-primary grid place-items-center font-display text-5xl flex-shrink-0">
            {(u.full_name || "P")[0]}
          </div>
        </div>

        {u.publications?.length > 0 && (
          <Section icon={BookOpen} title="Selected Publications">
            <ol className="space-y-3 list-decimal pl-6">
              {u.publications.map((pb, i) => (
                <li key={i}><b>{pb.title}</b>{pb.venue ? `, ${pb.venue}` : ""}{pb.year ? ` (${pb.year})` : ""}{pb.doi ? <span className="text-muted-foreground text-sm"> · {pb.doi}</span> : null}</li>
              ))}
            </ol>
          </Section>
        )}

        {u.past_projects?.length > 0 && (
          <Section icon={Award} title="Past Projects">
            <div className="space-y-3">
              {u.past_projects.map((p, i) => (
                <div key={i} className="border-l-2 border-primary/40 pl-4">
                  <div className="font-medium">{p.title}</div>
                  <div className="text-sm text-muted-foreground">{p.role}{p.duration ? ` · ${p.duration}` : ""}</div>
                  {p.summary && <div className="text-sm mt-1">{p.summary}</div>}
                </div>
              ))}
            </div>
          </Section>
        )}

        {u.past_funding?.length > 0 && (
          <Section icon={Coins} title="Grants Received">
            <div className="grid md:grid-cols-2 gap-3">
              {u.past_funding.map((f, i) => (
                <div key={i} className="border border-border rounded-md p-4">
                  <div className="overline">{f.agency}</div>
                  <div className="mt-1 font-medium">{f.grant_title}</div>
                  <div className="text-sm text-muted-foreground mt-1">{f.amount}{f.year ? ` · ${f.year}` : ""}{f.role ? ` · ${f.role}` : ""}</div>
                </div>
              ))}
            </div>
          </Section>
        )}

        <footer className="mt-14 pt-6 border-t border-border text-sm text-muted-foreground">
          Curated on <span className="font-medium text-foreground">GrantForge</span> · <Link to="/" className="text-primary hover:underline">Create yours</Link>
        </footer>
      </main>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <section className="mb-10">
      <h2 className="font-display text-3xl mb-4 flex items-center gap-2"><Icon className="w-6 h-6 text-primary"/>{title}</h2>
      {children}
    </section>
  );
}
